<template>
  <a-modal
    :visible="visible"
    :title="$t('tresorerie.nouveauBordereau')"
    width="70%"
    :footer="null"
    @cancel="handleCancel"
    :confirmLoading="loading"
  >
    <a-form-model
      ref="createForm"
      :model="form"
      :rules="rules"
      :label-col="{ span: 3 }"
      :wrapper-col="{ span: 21 }"
    >
      <!-- Type de bordereau -->
      <a-form-model-item :label="$t('tresorerie.type')" prop="type">
        <a-select
          v-model="form.type"
          :placeholder="$t('tresorerie.selectionnerTypeBordereau')"
          :disabled="loading"
          @change="onTypeChange"
        >
          <a-select-option value="CHEQUE">
            <a-icon type="bank" style="color: #1890ff" />
            {{ $t("tresorerie.cheques") }}
          </a-select-option>
          <a-select-option value="TRAITE">
            <a-icon type="file-text" style="color: #52c41a" />
            {{ $t("tresorerie.traites") }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <!-- Caisse -->
      <a-form-model-item :label="$t('tresorerie.caisse')" prop="caisseBank">
        <a-select
          v-model="form.caisseBank"
          :placeholder="$t('tresorerie.selectionnerCaisse')"
          :disabled="loading"
          :loading="caissesLoading"
        >
          <a-select-option
            v-for="caisse in caisses"
            :key="caisse._id"
            :value="caisse._id"
          >
            {{ caisse.name }} ({{ caisse.reference }})
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <!-- Date de dépôt prévue -->
      <a-form-model-item
        :label="$t('tresorerie.dateDepotPrevue')"
        prop="dateDepotPrevue"
      >
        <a-date-picker
          v-model="form.dateDepotPrevue"
          :placeholder="$t('tresorerie.dateDepotPrevuePlaceholder')"
          format="DD/MM/YYYY"
          style="width: 100%"
          :disabled="loading"
        />
      </a-form-model-item>

      <!-- Chèques/Traites -->
      <a-form-model-item :label="typeLabel" prop="cheques">
        <div style="margin-bottom: 8px">
          <a-button
            type="link"
            size="small"
            @click="selectAllCheques"
            :disabled="loading || availableCheques.length === 0"
          >
            {{ $t("tresorerie.toutSelectionner") }}
          </a-button>
          <a-button
            type="link"
            size="small"
            @click="deselectAllCheques"
            :disabled="loading || form.cheques.length === 0"
            style="margin-left: 8px"
          >
            {{ $t("tresorerie.toutDeselectionner") }}
          </a-button>
        </div>

        <!-- Search by multiple fields -->
        <div style="margin-bottom: 12px">
          <a-input-search
            v-model="searchTerm"
            :placeholder="searchPlaceholder"
            style="width: 400px"
            allow-clear
          >
            <a-icon slot="prefix" type="search" />
          </a-input-search>
        </div>

        <a-table
          :dataSource="sortedAvailableCheques"
          :columns="dynamicChequeColumns"
          :pagination="{ pageSize: 5, showSizeChanger: false }"
          size="small"
          :rowSelection="rowSelection"
          :loading="chequesLoading"
          :scroll="{ y: 200 }"
          rowKey="_id"
          style="border: 1px solid #d9d9d9; border-radius: 6px"
        >
          <template slot="bankStatus" slot-scope="text">
            <a-tag :color="getChequeStatutColor(text)">
              {{ $t("status." + text) }}
            </a-tag>
          </template>

          <template slot="montant" slot-scope="text">
            {{ formatAmount(text) }}
          </template>
        </a-table>

        <div style="margin-top: 8px; font-size: 12px; color: #666">
          <span v-if="form.cheques.length > 0">
            {{ form.cheques.length }}
            {{ typeLabel.toLowerCase() }} {{ $t("tresorerie.selectionnes") }}
          </span>
          <span v-else>
            {{ $t("tresorerie.aucunSelectionne") }}
            {{ typeLabel.toLowerCase().slice(0, -1) }}
            {{ $t("tresorerie.selectionnes") }}
          </span>
        </div>
      </a-form-model-item>
      <!-- Description -->
      <a-form-model-item
        :label="$t('tresorerie.description')"
        prop="description"
      >
        <a-textarea
          v-model="form.description"
          :placeholder="$t('tresorerie.descriptionBordereau')"
          :rows="3"
          :disabled="loading"
        />
      </a-form-model-item>
    </a-form-model>

    <!-- Action Buttons -->
    <div style="text-align: right; margin-top: 24px">
      <a-button @click="handleCancel" :disabled="loading">{{
        $t("all.cancel")
      }}</a-button>
      <a-button
        type="primary"
        @click="handleSubmit"
        :loading="loading"
        style="margin-left: 8px"
      >
        {{
          loading ? $t("tresorerie.creationEnCours") : $t("tresorerie.creer")
        }}
      </a-button>
    </div>
  </a-modal>
</template>

<script>
import apiClient from "@/services/axios";
import moment from "moment";
import { getChequeStatutColor } from "@/utilities/settings";
import { formatCurrency as formatCurrencyUtil, getBuildingCurrency } from "@/utilities/countrySpecific";
export default {
  name: "CreateBordereauModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      caissesLoading: false,
      chequesLoading: false,
      caisses: [],
      availableCheques: [],
      currency: null,
      form: {
        type: "CHEQUE",
        description: "",
        caisseBank: undefined,
        dateDepotPrevue: null,
        cheques: [],
      },
      searchTerm: "",
      rules: {
        type: [
          {
            required: true,
            message: this.$t("tresorerie.typeBordereauRequis"),
            trigger: "change",
          },
        ],
        caisseBank: [
          {
            required: true,
            message: this.$t("tresorerie.caisseRequise"),
            trigger: "change",
          },
        ],
        cheques: [
          {
            required: true,
            message: this.$t("tresorerie.auMoinsUnElementRequis"),
            trigger: "change",
          },
        ],
      },
    };
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.fetchCaisses();
        this.fetchAvailableCheques();
      }
    },
  },
  computed: {
    typeLabel() {
      return this.form.type === "CHEQUE"
        ? this.$t("tresorerie.cheques")
        : this.$t("tresorerie.traites");
    },
    searchPlaceholder() {
      return this.form.type === "CHEQUE"
        ? this.$t("tresorerie.rechercheParNumeroTitulaireBanque")
        : this.$t("tresorerie.rechercheParNumeroBanque");
    },
    // Dynamic columns based on bordereau type
    dynamicChequeColumns() {
      const baseColumns = [
        {
          title: this.$t("tresorerie.num"),
          dataIndex: "num",
          key: "num",
          width: 80,
          customRender: (text, record) => {
            const isSelected = this.form.cheques.includes(record._id);
            const isDisabled = record.bankStatus === "VALIDATED";
            return (
              <span
                style={{
                  fontWeight: isSelected ? "bold" : "normal",
                  color: isDisabled ? "#999" : "inherit",
                  textDecoration: isDisabled ? "line-through" : "none",
                }}
              >
                {text}
              </span>
            );
          },
        },
        {
          title: this.$t("tresorerie.banque"),
          dataIndex: "bank",
          key: "bank",
          customRender: (text, record) => {
            const isDisabled = record.bankStatus === "VALIDATED";
            return (
              <span
                style={{
                  color: isDisabled ? "#999" : "inherit",
                }}
              >
                {text && text.includes("-") ? text.split("-")[0] : text || "--"}
              </span>
            );
          },
        },
      ];

      // Add appropriate column based on bordereau type
      if (this.form.type === "CHEQUE") {
        baseColumns.push({
          title: this.$t("tresorerie.titulaire"),
          dataIndex: "titulaire",
          key: "titulaire",
          ellipsis: true,
          customRender: (text, record) => {
            const isDisabled = record.bankStatus === "VALIDATED";
            return (
              <span
                style={{
                  color: isDisabled ? "#999" : "inherit",
                }}
              >
                {text || "--"}
              </span>
            );
          },
        });

        // Add dateEchance column for CHEQUE type
        baseColumns.push({
          title: this.$t("recette.dateEcheance"),
          dataIndex: "dateEchance",
          key: "dateEchance",
          width: 100,
          customRender: (text, record) => {
            const isDisabled = record.bankStatus === "VALIDATED";
            const dateEchance = record.dateEchance;
            const displayDate =
              dateEchance && moment(dateEchance).isValid()
                ? moment(dateEchance).format("DD/MM/YYYY")
                : "--";

            return (
              <span
                style={{
                  color: isDisabled ? "#999" : "inherit",
                }}
              >
                {displayDate}
              </span>
            );
          },
        });
      } else {
        // For TRAITE, show RIB column
        baseColumns.push({
          title: this.$t("tresorerie.rib"),
          dataIndex: "rib",
          key: "rib",
          ellipsis: true,
          customRender: (text, record) => {
            const isDisabled = record.bankStatus === "VALIDATED";
            return (
              <span
                style={{
                  color: isDisabled ? "#999" : "inherit",
                }}
              >
                {text || "--"}
              </span>
            );
          },
        });
      }

      // Add remaining columns
      baseColumns.push(
        {
          title: this.$t("tresorerie.montant"),
          dataIndex: "montant",
          key: "montant",
          scopedSlots: { customRender: "montant" },
          width: 100,
        },
        {
          title: this.$t("tresorerie.statut"),
          dataIndex: "bankStatus",
          key: "bankStatus",
          scopedSlots: { customRender: "bankStatus" },
          width: 100,
        }
      );

      return baseColumns;
    },
    rowSelection() {
      return {
        selectedRowKeys: this.form.cheques,
        onChange: this.onChequeSelectionChange,
        getCheckboxProps: (record) => ({
          props: {
            disabled: record.bankStatus === "VALIDATED",
          },
        }),
      };
    },
    sortedAvailableCheques() {
      if (!this.availableCheques || this.availableCheques.length === 0) {
        return [];
      }

      // Filter by search term across multiple fields
      let filteredCheques = this.availableCheques;
      if (this.searchTerm && this.searchTerm.trim()) {
        const searchTerm = this.searchTerm.trim().toLowerCase();
        filteredCheques = this.availableCheques.filter((cheque) => {
          // Search in number field
          if (
            cheque.num &&
            cheque.num.toString().toLowerCase().includes(searchTerm)
          ) {
            return true;
          }

          // Search in titulaire field (for CHEQUE type)
          if (
            this.form.type === "CHEQUE" &&
            cheque.titulaire &&
            cheque.titulaire.toLowerCase().includes(searchTerm)
          ) {
            return true;
          }

          // Search in RIB field (for TRAITE type)
          if (
            this.form.type === "TRAITE" &&
            cheque.rib &&
            cheque.rib.toLowerCase().includes(searchTerm)
          ) {
            return true;
          }

          // Search in bank field
          if (cheque.bank && cheque.bank.toLowerCase().includes(searchTerm)) {
            return true;
          }

          return false;
        });
      }

      // Sort cheques: selected ones first, then by validation status, then by number
      return [...filteredCheques].sort((a, b) => {
        const aIsSelected = this.form.cheques.includes(a._id);
        const bIsSelected = this.form.cheques.includes(b._id);

        // First priority: selected cheques come first
        if (aIsSelected && !bIsSelected) return -1;
        if (!aIsSelected && bIsSelected) return 1;

        // Second priority: non-validated cheques come before validated ones
        const aIsValidated = a.bankStatus === "VALIDATED";
        const bIsValidated = b.bankStatus === "VALIDATED";
        if (!aIsValidated && bIsValidated) return -1;
        if (aIsValidated && !bIsValidated) return 1;

        // Third priority: sort by number
        return (a.num || "").localeCompare(b.num || "");
      });
    },
  },
  methods: {
    getChequeStatutColor,
    async fetchCaisses() {
      try {
        this.caissesLoading = true;
        const response = await apiClient.get("/compV3/caisse/bank");
        this.caisses = response.data || [];
      } catch (error) {
        console.error("Error fetching caisses:", error);
        this.$message.error(this.$t("tresorerie.erreurChargementCaisses"));
      } finally {
        this.caissesLoading = false;
      }
    },

    async fetchAvailableCheques() {
      try {
        this.chequesLoading = true;
        const response = await apiClient.get("/compV3/cheque-traite/active", {
          params: { type: this.form.type },
        });
        this.availableCheques = response.data.data || [];
      } catch (error) {
        console.error("Error fetching cheques:", error);
        this.$message.error(this.$t("tresorerie.erreurChargementElements"));
      } finally {
        this.chequesLoading = false;
      }
    },

    onTypeChange() {
      // Clear selected cheques when type changes
      this.form.cheques = [];
      // Clear search when type changes
      this.searchTerm = "";
      // Fetch new data based on selected type
      this.fetchAvailableCheques();
    },

    formatAmount(amount) {
      return formatCurrencyUtil(amount, this.currency);
    },

    onChequeSelectionChange(selectedRowKeys, selectedRows) {
      // Get the current validated cheques that should always remain selected
      const validatedCheques = this.availableCheques
        .filter((cheque) => cheque.bankStatus === "VALIDATED")
        .map((cheque) => cheque._id);

      // Ensure validated cheques are always included in the selection
      const finalSelection = [
        ...new Set([...selectedRowKeys, ...validatedCheques]),
      ];

      this.form.cheques = finalSelection;
    },

    selectAllCheques() {
      const selectableCheques = this.availableCheques
        .filter((cheque) => cheque.bankStatus !== "VALIDATED")
        .map((cheque) => cheque._id);

      // Get current validated cheques that should remain selected
      const validatedCheques = this.availableCheques
        .filter((cheque) => cheque.bankStatus === "VALIDATED")
        .map((cheque) => cheque._id);

      // Combine selectable cheques with validated cheques
      this.form.cheques = [
        ...new Set([...selectableCheques, ...validatedCheques]),
      ];
    },

    deselectAllCheques() {
      // Only deselect non-validated cheques, keep validated ones selected
      const validatedCheques = this.availableCheques
        .filter((cheque) => cheque.bankStatus === "VALIDATED")
        .map((cheque) => cheque._id);

      this.form.cheques = validatedCheques;
    },

    async handleSubmit() {
      try {
        // Validate form
        const valid = await this.$refs.createForm.validate();
        if (!valid) {
          return;
        }

        this.loading = true;

        // Prepare data for API
        const createData = {
          type: this.form.type,
          description: this.form.description,
          caisseBank: this.form.caisseBank,
          cheques: this.form.cheques,
        };

        // Add date if provided
        if (this.form.dateDepotPrevue) {
          createData.dateDepotPrevue = this.form.dateDepotPrevue.toISOString();
        }

        // Call API to create bordereau
        const response = await apiClient.post("/compV3/bordereau", createData);

        if (response.data.success) {
          this.$message.success(this.$t("tresorerie.bordereau.creeSucces"));
          this.$emit("created", response.data.data);
          this.handleCancel();
        } else {
          this.$message.error(
            response.data.message || this.$t("tresorerie.erreurCreationBordereau")
          );
        }
      } catch (error) {
        console.error("Error creating bordereau:", error);
        this.$message.error(
          error.response?.data?.message || this.$t("tresorerie.erreurCreationBordereau")
        );
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.$refs.createForm.resetFields();
      this.form = {
        type: "CHEQUE",
        description: "",
        caisseBank: undefined,
        dateDepotPrevue: null,
        cheques: [],
      };
      this.searchTerm = "";
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.ant-form-item {
  margin-bottom: 16px;
}

.ant-modal-body {
  padding: 24px;
}
</style>
